import { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import MovieCard from '@/components/base/MovieCard';
import { movies, movieCategories } from '@/mocks/movies';
import { Link } from 'react-router-dom';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroMovies = movies.filter(m => m.isHot).slice(0, 5);
  const latestMovies = movies.filter(m => m.isNew).slice(0, 8);
  const hotRanking = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % heroMovies.length);
  }, [heroMovies.length]);

  useEffect(() => {
    timerRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [nextSlide]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '桃子影院',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '桃子影院电影大全，最新电影热门电影排行榜华语电影在线看，永久免费不缓冲，高清超清蓝光4K影视秒开播放。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${typeof window !== 'undefined' ? window.location.origin : ''}/category/all?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '桃子影院真的永久免费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，桃子影院承诺永久免费为用户提供高清电影在线观看服务。我们通过广告收入维持运营，不会向用户收取任何观影费用，让每一位用户都能无门槛享受优质影视内容。',
        },
      },
      {
        '@type': 'Question',
        name: '桃子影院支持哪些画质？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃子影院提供高清、超清、蓝光和4K多种画质选择。系统会根据您的网络带宽自动匹配最佳画质，您也可以手动选择不同的清晰度，确保流畅的观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '观看电影需要注册账号吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。桃子影院所有电影资源均可在不注册账号的情况下直接观看，无需填写任何个人信息，打开网站即可秒开播放，方便快捷。',
        },
      },
      {
        '@type': 'Question',
        name: '桃子影院的电影资源更新快吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃子影院每天都会更新最新上映的电影资源，热门院线大片下线后会在第一时间上线。同时我们还有专业团队每天维护片库，确保所有电影资源都能正常播放。',
        },
      },
      {
        '@type': 'Question',
        name: '桃子影院支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桃子影院支持电脑、平板、手机等所有主流设备在线观看。我们的网站采用响应式设计，在不同屏幕尺寸下都能获得良好的观看体验，随时随地畅享电影。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="桃子影院-电影大全最新电影热门电影排行榜华语电影在线观看永久免费不缓冲秒开高清超清蓝光4K影视"
        description="桃子影院电影大全，最新电影热门电影排行榜华语电影在线看，永久免费不缓冲，高清超清蓝光4K影视秒开播放。涵盖动作片、喜剧片、爱情片、科幻片、恐怖片、动画片等海量电影资源。"
        keywords="电影大全,最新电影,热门电影,电影排行榜,华语电影,在线观看,免费,不缓冲,高清,超清,蓝光,4K影视,桃子影院"
        canonicalPath="/"
        jsonLd={{ ...jsonLd, ...faqJsonLd }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroMovies.map((movie, index) => (
            <div
              key={movie.id}
              className={`absolute inset-0 transition-opacity duration-800 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={movie.bannerImage}
                alt={movie.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>
            </div>
          ))}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center w-full px-4">
            <div className="text-center max-w-3xl">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-3 md:mb-4">
                <strong>桃子影院</strong> · 免费高清观影
              </h1>
              <p className="text-white/80 text-sm md:text-lg mb-6 md:mb-8 max-w-xl mx-auto">
                海量电影资源 · 永久免费 · 不缓冲秒开 · 高清超清蓝光4K画质
              </p>
              <div className="flex items-center justify-center gap-3 flex-col sm:flex-row">
                <Link
                  to="/category/all"
                  className="px-6 py-2.5 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  立即观影
                </Link>
                <Link
                  to="/category/huayu"
                  className="px-6 py-2.5 rounded-lg bg-white/20 text-white font-medium text-sm hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  华语电影
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {heroMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide ? 'w-6 bg-white' : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {movieCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-500 hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Latest Movies */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              <strong>最新上映</strong>
            </h2>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* Hot Ranking */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              <strong>热门排行榜</strong>
            </h2>
            <Link
              to="/category/all"
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {hotRanking.map((movie, index) => (
              <Link
                key={movie.id}
                to={`/movie/${movie.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-200 transition-colors cursor-pointer group"
              >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                  {index < 3 ? (
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      index === 0 ? 'bg-secondary-500' : index === 1 ? 'bg-accent-500' : 'bg-primary-400'
                    }`}>
                      {index + 1}
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-foreground-400">{index + 1}</span>
                  )}
                </div>
                <div className="w-10 h-14 flex-shrink-0 rounded overflow-hidden bg-background-200">
                  <img src={movie.coverImage} alt={movie.title} className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">{movie.subcategory} · {movie.year}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500 text-xs"></i>
                  <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">{movie.rating}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Category Showcase */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
            <strong>分类推荐</strong>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {movieCategories.filter(c => c.slug !== 'all').slice(0, 6).map((cat) => {
              const catMovies = movies.filter(m => m.category === cat.slug || m.subcategory === cat.name).slice(0, 3);
              return (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="block p-4 md:p-5 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors">
                      {cat.name}
                    </h3>
                  </div>
                  <div className="flex gap-2">
                    {catMovies.map(m => (
                      <div key={m.id} className="flex-1">
                        <div className="aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                          <img src={m.coverImage} alt={m.title} className="w-full h-full object-cover object-top" loading="lazy" />
                        </div>
                        <p className="text-xs text-foreground-600 mt-1 line-clamp-1">{m.title}</p>
                      </div>
                    ))}
                    {catMovies.length === 0 && (
                      <p className="text-xs text-foreground-400 py-6">暂无该分类电影</p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Features */}
        <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-shield-check-line', title: '永久免费', desc: '无需付费，畅享观影' },
              { icon: 'ri-flashlight-line', title: '秒开播放', desc: '不缓冲不卡顿' },
              { icon: 'ri-4k-line', title: '4K画质', desc: '高清超清蓝光4K' },
              { icon: 'ri-global-line', title: '海量片源', desc: '每日更新最新电影' },
            ].map((feature) => (
              <div key={feature.title} className="text-center p-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                  <i className={`${feature.icon} text-primary-500 text-xl`}></i>
                </div>
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{feature.title}</h3>
                <p className="text-xs text-foreground-500">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
            常见问题
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: '桃子影院真的永久免费吗？', a: '是的，桃子影院承诺永久免费为用户提供高清电影在线观看服务。我们通过广告收入维持运营，不会向用户收取任何观影费用，让每一位用户都能无门槛享受优质影视内容。' },
              { q: '桃子影院支持哪些画质？', a: '桃子影院提供高清、超清、蓝光和4K多种画质选择。系统会根据您的网络带宽自动匹配最佳画质，您也可以手动选择不同的清晰度，确保流畅的观影体验。' },
              { q: '观看电影需要注册账号吗？', a: '不需要。桃子影院所有电影资源均可在不注册账号的情况下直接观看，无需填写任何个人信息，打开网站即可秒开播放，方便快捷。' },
              { q: '桃子影院的电影资源更新快吗？', a: '桃子影院每天都会更新最新上映的电影资源，热门院线大片下线后会在第一时间上线。同时我们还有专业团队每天维护片库，确保所有电影资源都能正常播放。' },
              { q: '桃子影院支持哪些设备观看？', a: '桃子影院支持电脑、平板、手机等所有主流设备在线观看。我们的网站采用响应式设计，在不同屏幕尺寸下都能获得良好的观看体验，随时随地畅享电影。' },
            ].map((faq, index) => (
              <details key={index} className="group rounded-lg border border-background-200 overflow-hidden">
                <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-background-100 transition-colors">
                  <span className="font-medium text-sm text-foreground-900 pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}